import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy,
  Play,
  RotateCcw,
  Users,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Shuffle,
  Undo2,
  Sparkles,
  Ticket,
  Award,
  Flame,
  CheckCircle2,
  Clock,
  Printer
} from 'lucide-react';
import { Card, Button, Badge } from '../ui';
import { useNodeRoster } from '../../hooks/useNodeRoster';
import { NodeRosterPickerModal } from './common/NodeRosterPickerModal';
import { DirectoryMember } from '../../types';
import toast from 'react-hot-toast';
import { cn } from '../../lib/utils';

// Web Audio API sound synthesizers
const playTickTone = (ctx: AudioContext, freq = 600, duration = 0.04) => {
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch {
    // Web Audio failure: skipping the sound is acceptable.
  }
};

const playEliminateTone = (ctx: AudioContext) => {
  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(320, now);
    osc.frequency.exponentialRampToValueAtTime(110, now + 0.35);
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.35);
  } catch {
    // Web Audio failure: skipping the sound is acceptable.
  }
};

const playMilestoneFanfare = (ctx: AudioContext) => {
  try {
    const now = ctx.currentTime;
    [440, 554.37, 659.25, 880].forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = now + idx * 0.08;
      gain.gain.setValueAtTime(0.15, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.4);
    });
  } catch {
    // Web Audio failure: skipping the sound is acceptable.
  }
};

const playJackpotVictory = (ctx: AudioContext) => {
  try {
    const now = ctx.currentTime;
    const chords = [
      [523.25, 659.25, 783.99], // C
      [587.33, 739.99, 880.00], // D
      [659.25, 830.61, 987.77], // E
      [1046.50, 1318.51, 1567.98] // C high
    ];
    chords.forEach((chord, cIdx) => {
      chord.forEach(freq => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.value = freq;
        const start = now + cIdx * 0.22;
        gain.gain.setValueAtTime(0.18, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.9);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.9);
      });
    });
  } catch {
    // Web Audio failure: skipping the sound is acceptable.
  }
};

interface TicketEntry {
  id: number;
  label: string;
  isEliminated: boolean;
  eliminatedOrder?: number;
}

export function ReverseRaffleBoard() {
  const [ticketCount, setTicketCount] = useState<number>(100);
  const [customNames, setCustomNames] = useState<string>('');
  const [useNames, setUseNames] = useState<boolean>(false);
  const [milestoneStep, setMilestoneStep] = useState<number>(25); // Every 25th eliminated wins prize
  const [raffleTitle, setRaffleTitle] = useState<string>('Annual Booster Club Reverse Raffle');

  // Live state
  const [tickets, setTickets] = useState<TicketEntry[]>(() => {
    return Array.from({ length: 100 }, (_, i) => ({
      id: i + 1,
      label: `#${i + 1}`,
      isEliminated: false
    }));
  });

  const [eliminationHistory, setEliminationHistory] = useState<TicketEntry[]>([]);
  const [lastEliminated, setLastEliminated] = useState<TicketEntry | null>(null);
  const [milestoneAlert, setMilestoneAlert] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [isSoundOn, setIsSoundOn] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isRosterModalOpen, setIsRosterModalOpen] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const { hasActiveNode, nodeName } = useNodeRoster();

  // Initialize or update tickets
  const initializeBoard = (count = ticketCount, useNameList = useNames, namesText = customNames) => {
    let newTickets: TicketEntry[] = [];
    if (useNameList && namesText.trim()) {
      const names = namesText.split('\n').map(n => n.trim()).filter(Boolean);
      newTickets = names.map((name, idx) => ({
        id: idx + 1,
        label: name,
        isEliminated: false
      }));
      setTicketCount(names.length);
    } else {
      newTickets = Array.from({ length: count }, (_, i) => ({
        id: i + 1,
        label: `#${i + 1}`,
        isEliminated: false
      }));
    }
    setTickets(newTickets);
    setEliminationHistory([]);
    setLastEliminated(null);
    setMilestoneAlert(null);
  };

  const getAudio = () => {
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  };

  const remainingTickets = useMemo(() => {
    return tickets.filter(t => !t.isEliminated);
  }, [tickets]);

  const isGrandWinner = remainingTickets.length === 1 && tickets.length > 1;
  const isFinalFive = remainingTickets.length > 1 && remainingTickets.length <= 5;

  const eliminateTicket = (ticketId: number) => {
    const target = tickets.find(t => t.id === ticketId);
    if (!target || target.isEliminated || remainingTickets.length <= 1) return;

    const newOrder = eliminationHistory.length + 1;
    const updatedTicket: TicketEntry = {
      ...target,
      isEliminated: true,
      eliminatedOrder: newOrder
    };

    setTickets(prev => prev.map(t => t.id === ticketId ? updatedTicket : t));
    setEliminationHistory(prev => [updatedTicket, ...prev]);
    setLastEliminated(updatedTicket);

    const ctx = isSoundOn ? getAudio() : null;

    // Check Milestone or Grand Winner
    const remainingCount = remainingTickets.length - 1;
    if (remainingCount === 1) {
      if (ctx) playJackpotVictory(ctx);
      setMilestoneAlert(`🏆 GRAND PRIZE WINNER DETERMINED!`);
    } else if (milestoneStep > 0 && newOrder % milestoneStep === 0) {
      if (ctx) playMilestoneFanfare(ctx);
      setMilestoneAlert(`🎁 MILESTONE! Ticket ${updatedTicket.label} is the #${newOrder} eliminated ticket (Door Prize Winner!)`);
    } else {
      if (ctx) playEliminateTone(ctx);
      setMilestoneAlert(null);
    }
  };

  // Random automatic draw with suspense animation
  const drawRandomElimination = () => {
    if (remainingTickets.length <= 1 || isDrawing) return;
    setIsDrawing(true);
    const ctx = isSoundOn ? getAudio() : null;

    let ticks = 0;
    const maxTicks = 12;
    const interval = setInterval(() => {
      ticks++;
      if (ctx) playTickTone(ctx, 400 + ticks * 25, 0.03);
      if (ticks >= maxTicks) {
        clearInterval(interval);
        const randomIndex = Math.floor(Math.random() * remainingTickets.length);
        const chosen = remainingTickets[randomIndex];
        eliminateTicket(chosen.id);
        setIsDrawing(false);
      }
    }, 75);
  };

  const undoLastElimination = () => {
    if (eliminationHistory.length === 0) return;
    const [last, ...rest] = eliminationHistory;
    setTickets(prev => prev.map(t => t.id === last.id ? { ...t, isEliminated: false, eliminatedOrder: undefined } : t));
    setEliminationHistory(rest);
    setLastEliminated(rest[0] || null);
    setMilestoneAlert(null);
    toast.success(`Restored ${last.label} to the board`);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const handleImportRoster = (members: DirectoryMember[]) => {
    if (members.length === 0) return;
    const names = members.map(m => m.name).filter(Boolean);
    setCustomNames(names.join('\n'));
    setUseNames(true);
    initializeBoard(names.length, true, names.join('\n'));
    toast.success(`Imported ${names.length} members from organization roster`);
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'w-full text-left font-sans transition-colors',
        isFullscreen ? 'bg-black text-white p-6 min-h-screen flex flex-col justify-between overflow-y-auto' : 'space-y-8'
      )}
    >
      {/* Top Banner / Setup Strip */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b-4 border-black dark:border-white pb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="bg-[#FF0055] text-white text-[10px] font-black uppercase tracking-widest px-2 py-0.5 border border-black dark:border-white shadow-[2px_2px_0px_#000]">
              Elimination Draw
            </span>
            {isFinalFive && (
              <span className="bg-[#FFD600] text-black text-[10px] font-black uppercase tracking-widest px-2 py-0.5 border border-black animate-pulse shadow-[2px_2px_0px_#000]">
                🔥 Final 5 Tickets!
              </span>
            )}
            {isGrandWinner && (
              <span className="bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest px-2 py-0.5 border border-black shadow-[2px_2px_0px_#000]">
                🏆 Grand Prize Winner!
              </span>
            )}
          </div>
          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tighter font-display text-black dark:text-white">
            {raffleTitle}
          </h2>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setIsSoundOn(!isSoundOn)}
            className="p-2 border-2 border-black dark:border-white bg-white dark:bg-black text-black dark:text-white shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#fff] cursor-pointer"
            title={isSoundOn ? 'Mute sound effects' : 'Unmute sound effects'}
          >
            {isSoundOn ? <Volume2 className="w-4 h-4 text-[#0044FF]" /> : <VolumeX className="w-4 h-4 text-black/40 dark:text-white/40" />}
          </button>

          <button
            onClick={toggleFullscreen}
            className="p-2 border-2 border-black dark:border-white bg-white dark:bg-black text-black dark:text-white shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#fff] cursor-pointer"
            title="Projector / Fullscreen Mode"
          >
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>

          <button
            onClick={undoLastElimination}
            disabled={eliminationHistory.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 border-2 border-black dark:border-white bg-white dark:bg-black text-black dark:text-white text-xs font-black uppercase tracking-wider disabled:opacity-30 shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#fff] cursor-pointer"
          >
            <Undo2 className="w-3.5 h-3.5" /> Undo
          </button>

          <button
            onClick={() => initializeBoard()}
            className="flex items-center gap-1.5 px-3 py-2 border-2 border-black dark:border-white bg-white dark:bg-black text-black dark:text-white text-xs font-black uppercase tracking-wider shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#fff] hover:bg-red-500 hover:text-white transition-colors cursor-pointer"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
        </div>
      </div>

      {/* Main Game Stage / Scoreboard */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="p-4 border-4 border-black dark:border-white bg-white dark:bg-black shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]">
          <span className="text-[10px] uppercase font-black tracking-widest text-black/50 dark:text-white/50 block mb-1">
            Still In Play
          </span>
          <div className="text-3xl md:text-4xl font-black font-mono text-[#0044FF]">
            {remainingTickets.length} <span className="text-sm text-black/40 dark:text-white/40 font-normal">/ {tickets.length}</span>
          </div>
          <span className="text-[9px] font-bold text-black/50 dark:text-white/50">
            {Math.round((remainingTickets.length / tickets.length) * 100)}% remaining
          </span>
        </div>

        <div className="p-4 border-4 border-black dark:border-white bg-white dark:bg-black shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]">
          <span className="text-[10px] uppercase font-black tracking-widest text-black/50 dark:text-white/50 block mb-1">
            Eliminated
          </span>
          <div className="text-3xl md:text-4xl font-black font-mono text-[#FF0055]">
            {eliminationHistory.length}
          </div>
          <span className="text-[9px] font-bold text-black/50 dark:text-white/50">
            Tickets drawn
          </span>
        </div>

        <div className="p-4 border-4 border-black dark:border-white bg-white dark:bg-black shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]">
          <span className="text-[10px] uppercase font-black tracking-widest text-black/50 dark:text-white/50 block mb-1">
            Just Out
          </span>
          <div className="text-xl md:text-2xl font-black font-mono truncate text-black dark:text-white">
            {lastEliminated ? lastEliminated.label : '—'}
          </div>
          <span className="text-[9px] font-bold text-black/40 dark:text-white/40">
            {lastEliminated ? `#${lastEliminated.eliminatedOrder} out` : 'Waiting for draw'}
          </span>
        </div>

        <div className="p-4 border-4 border-black dark:border-white bg-white dark:bg-black shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff] flex flex-col justify-center">
          <button
            onClick={drawRandomElimination}
            disabled={remainingTickets.length <= 1 || isDrawing}
            className="w-full h-full min-h-[50px] bg-[#FFD600] text-black border-2 border-black font-black uppercase text-xs tracking-wider shadow-[3px_3px_0px_#000] hover:bg-[#0044FF] hover:text-white disabled:opacity-30 transition-all cursor-pointer flex items-center justify-center gap-2"
          >
            <Shuffle className={cn('w-4 h-4', isDrawing && 'animate-spin')} />
            {isDrawing ? 'Drawing...' : 'Draw & Eliminate'}
          </button>
        </div>
      </div>

      {/* Milestone / Winner Alert Banner */}
      {milestoneAlert && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 border-4 border-black dark:border-white bg-[#FFD600] text-black font-black text-sm uppercase tracking-wide shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-black shrink-0" />
            <span>{milestoneAlert}</span>
          </div>
          <button
            onClick={() => setMilestoneAlert(null)}
            className="text-xs font-bold underline cursor-pointer ml-4"
          >
            Dismiss
          </button>
        </motion.div>
      )}

      {/* Grand Winner Spotlight */}
      {isGrandWinner && (
        <div className="p-8 border-8 border-black dark:border-white bg-[#0044FF] text-white text-center shadow-[10px_10px_0px_#FFD600] space-y-4">
          <Trophy className="w-16 h-16 mx-auto text-[#FFD600] animate-bounce" />
          <h3 className="text-xl md:text-3xl font-black uppercase tracking-tight">
            Last Ticket Remaining — Grand Champion!
          </h3>
          <div className="text-5xl md:text-7xl font-black font-mono tracking-tighter bg-white text-black py-4 px-8 inline-block border-4 border-black shadow-[6px_6px_0px_#000]">
            {remainingTickets[0]?.label}
          </div>
          <p className="text-sm font-bold text-white/80">
            Survived all {tickets.length - 1} eliminations!
          </p>
        </div>
      )}

      {/* THE BIG ELIMINATION BOARD (Grid) */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-xs font-black uppercase tracking-widest text-black/60 dark:text-white/60">
            Big Board (Click any ticket to manually eliminate)
          </span>
          <span className="text-[10px] font-mono text-black/40 dark:text-white/40">
            {tickets.length} Tickets
          </span>
        </div>

        <div className={cn(
          'grid gap-2 p-4 border-4 border-black dark:border-white bg-[#F5F5F0] dark:bg-[#0A0A0A] shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff]',
          tickets.length <= 50 ? 'grid-cols-5 sm:grid-cols-10' :
          tickets.length <= 100 ? 'grid-cols-5 sm:grid-cols-10 md:grid-cols-10' :
          'grid-cols-6 sm:grid-cols-10 md:grid-cols-12 lg:grid-cols-15'
        )}>
          {tickets.map((t) => {
            const isElim = t.isEliminated;
            return (
              <button
                key={t.id}
                onClick={() => eliminateTicket(t.id)}
                disabled={isElim || remainingTickets.length <= 1}
                className={cn(
                  'h-12 flex flex-col items-center justify-center p-1 border-2 font-mono font-black text-xs transition-all relative select-none rounded-none cursor-pointer',
                  isElim
                    ? 'border-red-500/30 bg-red-500/10 text-red-500/40 line-through opacity-40 hover:opacity-70'
                    : isFinalFive
                      ? 'border-[#0044FF] bg-[#0044FF] text-white shadow-[3px_3px_0px_#FFD600] scale-[1.03] animate-pulse'
                      : 'border-black dark:border-white bg-white dark:bg-black text-black dark:text-white hover:bg-[#FFD600] hover:text-black shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#fff] active:translate-y-0.5'
                )}
                title={isElim ? `Eliminated #${t.eliminatedOrder}` : `Click to eliminate ${t.label}`}
              >
                <span className="truncate max-w-full leading-tight">{t.label}</span>
                {isElim && (
                  <span className="text-[8px] font-mono text-red-600 block">
                    #{t.eliminatedOrder}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Bottom Setup & Graveyard Accordion (Hidden in fullscreen) */}
      {!isFullscreen && (
        <div className="grid lg:grid-cols-12 gap-6 pt-4 border-t-4 border-black dark:border-white">
          {/* Board Setup & Presets */}
          <div className="lg:col-span-6 space-y-4">
            <Card className="p-6 border-4 border-black dark:border-white rounded-none bg-white dark:bg-black shadow-[6px_6px_0px_#000] dark:shadow-[6px_6px_0px_#fff] space-y-4">
              <h3 className="text-sm font-black uppercase tracking-wider font-display border-b-2 border-black dark:border-white pb-2 flex items-center gap-2">
                <Ticket className="w-4 h-4 text-[#0044FF]" /> Raffle Setup & Ticket Pools
              </h3>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest block mb-1">
                  Raffle Event Title
                </label>
                <input
                  type="text"
                  value={raffleTitle}
                  onChange={e => setRaffleTitle(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-bold text-xs"
                />
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest block mb-1">
                  Preset Ticket Pool Count
                </label>
                <div className="flex gap-2 flex-wrap">
                  {[25, 50, 100, 150, 200].map(cnt => (
                    <button
                      key={cnt}
                      onClick={() => {
                        setTicketCount(cnt);
                        setUseNames(false);
                        initializeBoard(cnt, false);
                      }}
                      className={cn(
                        'px-3 py-1.5 text-xs font-mono font-black border-2 border-black dark:border-white transition-all cursor-pointer',
                        ticketCount === cnt && !useNames
                          ? 'bg-[#0044FF] text-white shadow-[2px_2px_0px_#FFD600]'
                          : 'bg-white dark:bg-black text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5'
                      )}
                    >
                      {cnt} Tickets
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest block mb-1">
                  Door Prize Milestone Step
                </label>
                <select
                  value={milestoneStep}
                  onChange={e => setMilestoneStep(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-bold text-xs"
                >
                  <option value={0}>No Milestone Prizes</option>
                  <option value={10}>Every 10th ticket out wins door prize</option>
                  <option value={25}>Every 25th ticket out wins door prize</option>
                  <option value={50}>Every 50th ticket out wins door prize</option>
                </select>
              </div>

              {/* Custom Names / Roster Import */}
              <div className="pt-2 border-t space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-black uppercase tracking-widest">
                    Or Use Custom Participant Names
                  </label>
                  {hasActiveNode && (
                    <button
                      onClick={() => setIsRosterModalOpen(true)}
                      className="text-[10px] font-black uppercase tracking-wider text-[#0044FF] hover:underline cursor-pointer"
                    >
                      + Import {nodeName || 'Org'} Roster
                    </button>
                  )}
                </div>
                <textarea
                  rows={3}
                  placeholder="Paste one ticket holder name per line..."
                  value={customNames}
                  onChange={e => setCustomNames(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] font-mono text-xs"
                />
                <button
                  onClick={() => {
                    setUseNames(true);
                    initializeBoard(undefined, true, customNames);
                  }}
                  disabled={!customNames.trim()}
                  className="px-4 py-2 border-2 border-black dark:border-white bg-black text-white dark:bg-white dark:text-black font-black uppercase text-xs tracking-wider shadow-[2px_2px_0px_#FFD600] disabled:opacity-30 cursor-pointer"
                >
                  Apply Custom Names List
                </button>
              </div>
            </Card>
          </div>

          {/* Elimination Order Graveyard */}
          <div className="lg:col-span-6 space-y-4">
            <Card className="p-6 border-4 border-black dark:border-white rounded-none bg-white dark:bg-black shadow-[6px_6px_0px_#000] dark:shadow-[6px_6px_0px_#fff] space-y-3">
              <div className="flex justify-between items-center border-b-2 border-black dark:border-white pb-2">
                <h3 className="text-sm font-black uppercase tracking-wider font-display flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#FF0055]" /> Elimination Graveyard Log
                </h3>
                <span className="text-xs font-mono font-bold">{eliminationHistory.length} Out</span>
              </div>

              {eliminationHistory.length === 0 ? (
                <div className="text-xs font-mono text-black/40 dark:text-white/40 py-8 text-center">
                  No tickets eliminated yet. Hit "Draw & Eliminate" or click tickets above to start the raffle!
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto space-y-1 pr-1 font-mono text-xs">
                  {eliminationHistory.map((t) => (
                    <div
                      key={t.id}
                      className="p-2 border border-black/10 dark:border-white/10 flex justify-between items-center bg-black/5 dark:bg-white/5"
                    >
                      <span className="font-bold text-[#FF0055]">#{t.eliminatedOrder} Out</span>
                      <span className="font-bold truncate max-w-[200px]">{t.label}</span>
                      <span className="text-[10px] text-black/40 dark:text-white/40">Ticket ID {t.id}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* Organization Roster Picker Modal */}
      <NodeRosterPickerModal
        isOpen={isRosterModalOpen}
        onClose={() => setIsRosterModalOpen(false)}
        onImport={handleImportRoster}
        title="Import Reverse Raffle Participants"
      />
    </div>
  );
}
